import { faCheck, faEye, faEyeSlash, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import BrianyCareLogo from '../../assets/auth/BrainyCare-Logo.jpg';
import api from "../../api/api";
import { useNavigate, useParams } from "react-router-dom";
import ToastMessage from "../../utils/mui/righttoptoast";
import { getPracticeId, practiceName, practiceFullName, practiceLogo, logoShowsName } from "../../config/practice";

const ExpireResetPassword = () => {
  const { token, id } = useParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [alertmessage, setAlertMessage] = useState('');
  const [severity, setSeverity] = useState('success');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const practiseid = getPracticeId();

  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    containsNumber: false,
    containsUppercase: false,
    containsLowercase: false,
    containsSpecialChar: false,
  });

  const [buttonText, setButtonText] = useState('Submit');

  useEffect(() => {
    if (!id) {
      setAlertMessage('Invalid or missing ID. Redirecting to login...');
      setSeverity('error');
      setOpen(true);
      setTimeout(() => {
        navigate('/login');
      }, 4000);
    }
  }, [id, navigate]);

  const handlealert = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  const validatePassword = (password) => {
    setPasswordRequirements({
      minLength: password.length >= 6,
      containsNumber: /\d/.test(password),
      containsUppercase: /[A-Z]/.test(password),
      containsLowercase: /[a-z]/.test(password),
      containsSpecialChar: /[!@#$%^&*]/.test(password),
    });
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    validatePassword(password);
  };

  const handleConfirmPasswordChange = (e) => {
    const password = e.target.value;
    setConfirmPassword(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setButtonText('Submitting...');
    setPasswordMatch(true);

    if (newPassword === confirmPassword) {
      try {
        const response = await api.put(`/v1/${practiseid}/auth/update/user-password`, {
          rowid: id,
          oldPassword: oldPassword,
          newPassword: newPassword
        });

        if (response.status === 200) {
          setAlertMessage('Password reset was successful');
          setSeverity('success');
          setOpen(true);
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } else if (response.status === 201) {
          setAlertMessage(response.message );
          setSeverity('info');
          setOpen(true);
        } else {
          setAlertMessage('Something went wrong');
          setSeverity('error');
          setOpen(true);
        }
      } catch (error) {
        console.error(error);
        setAlertMessage('Something went wrong');
        setSeverity('error');
        setOpen(true);
      } finally {
        setButtonText('Submit');
      }
    } else {
      setPasswordMatch(false);
      setButtonText('Submit');
    }
  };

  const isSubmitDisabled = !(
    passwordRequirements.minLength &&
    passwordRequirements.containsNumber &&
    passwordRequirements.containsUppercase &&
    passwordRequirements.containsLowercase &&
    passwordRequirements.containsSpecialChar &&
    newPassword === confirmPassword &&
    oldPassword.trim().length > 0
  );

  return (
    <>
      <div className="bg-[#1d2951] flex justify-center items-center w-full h-[100vh]">
        <div className="bg-white rounded-3xl p-10 flex flex-col lg:w-[800px] sm:w-[90%] w-full shadow-lg">
          <div className="flex w-full justify-center">
            <div>
              <img src={practiceLogo(BrianyCareLogo)} alt={`${practiceName()} Logo`} className={logoShowsName() ? "h-24 mb-4" : "w-16 mb-4"} />
            </div>
            {/* A logo that carries the name (7825) stands alone. */}
            {!logoShowsName() && (
            <div className="flex flex-col ml-2 items-start">
              <div className="text-[32px] font-black text-[#1e90ff] leading-none" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {practiceName()}
              </div>
              <div className="text-xs font-bold ml-1 mt-2">
                {practiceFullName()}
              </div>
            </div>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="flex flex-col lg:flex-row gap-6 mb-6 items-center">
              <div className="lg:w-1/2 sm:w-full w-full">
                <div className="mb-4 flex flex-col">
                  <label htmlFor="old-password" className="text-sm text-left mb-2">Old Password</label>
                  <div className='relative'>
                    <input
                      type="password"
                      id="old-password"
                      placeholder="Enter old password"
                      className="border border-gray-300 rounded-md p-2 text-sm w-full focus:outline-none focus:ring-0"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mb-4 flex flex-col">
                  <label htmlFor="new-password" className="text-sm text-left mb-2">New Password</label>
                  <div className='relative'>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="new-password"
                      placeholder="Enter new password"
                      className="border border-gray-300 rounded-md p-2 text-sm w-full focus:outline-none focus:ring-0"
                      value={newPassword}
                      onChange={handlePasswordChange}
                    />
                    <FontAwesomeIcon
                      icon={showPassword ? faEye : faEyeSlash}
                      onClick={togglePasswordVisibility}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500"
                    />
                  </div>
                </div>

                <div className="mb-4 flex flex-col">
                  <label htmlFor="confirm-password" className="text-sm text-left mb-2">Confirm Password</label>
                  <div className='relative'>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirm-password"
                      placeholder="Confirm your password"
                      className="border border-gray-300 rounded-md p-2 text-sm w-full focus:outline-none focus:ring-0"
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                    />
                    <FontAwesomeIcon
                      icon={showConfirmPassword ? faEye : faEyeSlash}
                      onClick={toggleConfirmPasswordVisibility}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500"
                    />
                  </div>
                </div>
              </div>

              <div className="lg:w-1/2 sm:w-full w-full">
                <div className="bg-white p-2 h-auto rounded-lg border border-gray-300 shadow-sm">
                  <div className="bg-[#1e90ff] p-1 -mt-2 -mx-2 rounded-t-sm">
                    <h3 className="text-lg font-semibold text-white">Password Requirements</h3>
                  </div>
                  <ul className="mt-4 text-sm text-gray-600">
                    <li className="flex items-center text-left">
                      <FontAwesomeIcon icon={passwordRequirements.minLength ? faCheck : faTimes} className={passwordRequirements.minLength ? 'text-green-500 mr-2' : 'text-red-500 mr-2'} />
                      Must contain at least 6 characters
                    </li>
                    <li className="flex items-center text-left">
                      <FontAwesomeIcon icon={passwordRequirements.containsNumber ? faCheck : faTimes} className={passwordRequirements.containsNumber ? 'text-green-500 mr-2' : 'text-red-500 mr-2'} />
                      Must contain at least 1 numeric value (1-9)
                    </li>
                    <li className="flex items-center text-left">
                      <FontAwesomeIcon icon={passwordRequirements.containsUppercase ? faCheck : faTimes} className={passwordRequirements.containsUppercase ? 'text-green-500 mr-2' : 'text-red-500 mr-2'} />
                      Must contain at least 1 uppercase letter
                    </li>
                    <li className="flex items-center text-left">
                      <FontAwesomeIcon icon={passwordRequirements.containsLowercase ? faCheck : faTimes} className={passwordRequirements.containsLowercase ? 'text-green-500 mr-2' : 'text-red-500 mr-2'} />
                      Must contain at least 1 lowercase letter
                    </li>
                    <li className="flex items-center text-left">
                      <FontAwesomeIcon icon={passwordRequirements.containsSpecialChar ? faCheck : faTimes} className={passwordRequirements.containsSpecialChar ? 'text-green-500 mr-2' : 'text-red-500 mr-2'} />
                      Must contain at least 1 special character (!@#$%^&*)
                    </li>
                    <li className="flex items-center text-left">
                      <FontAwesomeIcon icon={newPassword === confirmPassword ? faCheck : faTimes} className={newPassword === confirmPassword ? 'text-green-500 mr-2' : 'text-red-500 mr-2'} />
                      Password and confirm password should be same
                    </li>
                  </ul>
                </div>
              </div>
            </div>
   <div
      onClick={() => navigate('/forgotpassword/?flow=expire')}
      className="text-blue-600 text-start font-sm font-normal hover:underline cursor-pointer"
    >
      Another Way To Reset
    </div>
            <div className="w-full">
              <button
                type="submit"
                className="bg-[#E17954] w-full text-white px-3 py-2 text-sm rounded-3xl mt-4 focus:outline-none focus:ring-0"
                disabled={isSubmitDisabled}
              >
                {buttonText}
              </button>
            </div>
          </form>

          {!passwordMatch && (
            <div className="text-red-500 text-center mt-4">Passwords do not match. Please try again.</div>
          )}
        </div>
      </div>
      <ToastMessage message={alertmessage} severity={severity} open={open} handleClose={handlealert} />
    </>
  );
};

export default ExpireResetPassword;
